import React, { ReactNode, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import {
  Select,
  Input,
  Space,
  Button,
  Popconfirm,
  Modal,
  InputNumber,
  message,
  Divider,
  Alert,
} from "antd";
import { SyncLock as SyncLockIcon } from "@mui/icons-material";
import {
  getAllCatalogueChildrenbyParentCode,
  getAllProfilesNoPagination,
  userById,
  userResetPassword,
} from "../../../../../../libs/apis";
import { CatalogueI, ParameterI } from "../../../../../../libs/interfaces";
import { StringHelper } from "../../../../../../libs/helpers";
import {
  CATALOGUE_AREA_USER_CODE,
  CATALOGUE_TYPE_USER_CODE,
  CEDULA_CODE,
  CLIENTS_POSITIONS_CODE,
  COLOR_PRIMARY,
  PROFILE_TYPE_CLIENT_CODE,
  TYPE_USER_CLIENT_CODE,
} from "../../../../../../libs/constants";
import { useUserForm } from "../hook/useUserForm";
import { Controller } from "react-hook-form";

interface CreateUserPropsI {
  open: boolean;
  handleClose: () => void;
  id?: number | null;
  typesIdentification: ParameterI[];
  clientId: number;
}

export const CreateUser = ({
  open,
  handleClose,
  id,
  typesIdentification,
  clientId,
}: CreateUserPropsI) => {
  const [types, setTypes] = useState<CatalogueI[]>([]);
  const [areas, setAreas] = useState<CatalogueI[]>([]);
  const [positions, setPositions] = useState<CatalogueI[]>([]);
  const [confirmPassword, setConfirmPassword] = useState(false);

  const stringHelper = new StringHelper();
  const {
    register,
    handleSubmit,
    errors,
    setValue,
    onSubmit,
    control,
    reset,
    getValues,
    isProcessing,
  } = useUserForm({ id, onClose: handleClose, clientId });

  const form = getValues();

  useEffect(() => {
    handleGetData();
  }, []);

  const handleGetData = async () => {
    try {
      if (id) {
        const { data: dataUser } = await userById(id);
        if (!dataUser) return;

        reset(dataUser);
      }

      const { data: dataAreas } = await getAllCatalogueChildrenbyParentCode(
        CATALOGUE_AREA_USER_CODE
      );
      const { data: dataPositions } = await getAllCatalogueChildrenbyParentCode(
        CLIENTS_POSITIONS_CODE
      );
      if (!dataAreas) return;
      if (!dataPositions) return;
      const { data: dataTypes } = await getAllCatalogueChildrenbyParentCode(
        CATALOGUE_TYPE_USER_CODE
      );
      if (!dataTypes) return;
      const { data: dataProfile } = await getAllProfilesNoPagination(null);
      if (!dataProfile) return;

      setAreas(dataAreas);
      setPositions(dataPositions);
      const fillType = dataTypes.find(
        (el) => el.code === TYPE_USER_CLIENT_CODE
      );
      const fillTypeIdentification = typesIdentification.find(
        (el) => el.code === CEDULA_CODE
      );
      const fillProfile = dataProfile.find(
        (el) => el.code === PROFILE_TYPE_CLIENT_CODE
      );

      if (!id) {
        setValue("typeId", fillType!.id);
        setValue("docTypeId", fillTypeIdentification!.id);
        setValue("profileId", fillProfile!.id!);
      }
    } catch (err: any) {
      console.log(err);
      message.error(err.toString());
    }
  };

  const handleResetPassword = async () => {
    setConfirmPassword(true);
    try {
      const { data: dataUser } = await userResetPassword(form.id!);
      message.success(dataUser);
      setConfirmPassword(false);
    } catch (err: any) {
      setConfirmPassword(false);
      message.error(err.toString());
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={400}
      destroyOnClose
      centered
      okText={id ? "Editar" : "Crear"}
      cancelText="Cancelar"
      okButtonProps={{ loading: false }}
      cancelButtonProps={{ loading: false }}
      onOk={handleSubmit(onSubmit)}
      title={
        <p style={{ fontSize: 20, color: COLOR_PRIMARY, fontWeight: "bold" }}>
          FICHA DE USUARIO 📝
        </p>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          container
          spacing={1}
          sx={{ height: "440px", pt: 2 }}
          justifyContent="center"
          alignItems="flex-start"
          alignContent="flex-start"
        >
          <Grid item xs={12}>
            <Controller
              name="lastName"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  addonBefore={
                    <p style={{ width: "80px", fontWeight: "bold" }}>
                      Apellidos
                    </p>
                  }
                  variant="filled"
                  size="middle"
                  name="lastName"
                  value={field.value || ""}
                  status={field.value.length < 5 ? "error" : ""}
                  onChange={(e) => {
                    setValue("lastName", e.target.value);
                  }}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      setValue("lastName", e.target.value);
                      return;
                    }

                    const value_ = e.target.value
                      .trim()
                      .toLowerCase()
                      .split(" ")
                      .map((el) => stringHelper.capitalizeFirstLetter(el))
                      .join(" ");

                    setValue("lastName", value_);
                  }}
                />
              )}
            />
            {errors.lastName && (
                  <p style={{ color: "red" }}>
                    {(errors?.lastName?.message as ReactNode) ?? ""}
                  </p>
                )}
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  addonBefore={
                    <p style={{ width: "80px", fontWeight: "bold" }}>Nombres</p>
                  }
                  variant="filled"
                  size="middle"
                  name="firstName"
                  status={field.value.length < 2 ? "error" : ""}
                  value={field.value || ""}
                  onChange={(e) => {
                    setValue("firstName", e.target.value);
                  }}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      setValue("firstName", e.target.value);
                      return;
                    }

                    const value_ = e.target.value
                      .trim()
                      .toLowerCase()
                      .split(" ")
                      .map((el) => stringHelper.capitalizeFirstLetter(el))
                      .join(" ");

                    setValue("firstName", value_);
                  }}
                />
              )}
            />
             {errors.firstName && (
                  <p style={{ color: "red" }}>
                    {(errors?.firstName?.message as ReactNode) ?? ""}
                  </p>
                )}

          </Grid>
          <Grid item xs={12}>
            <Controller
              name="docNumber"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  addonBefore={
                    <p style={{ width: "80px", fontWeight: "bold" }}>Cédula</p>
                  }
                  variant="filled"
                  size="middle"
                  value={field.value || ""}
                  status={field.value.length < 10 ? "error" : ""}
                  onChange={(e) => {
                    setValue("docNumber", e.target.value.trim());
                  }}
                />
              )}
            />
             {errors.docNumber && (
                  <p style={{ color: "red" }}>
                    {(errors?.docNumber?.message as ReactNode) ?? ""}
                  </p>
                )}

          </Grid>
          <Grid item xs={12}>
            <Controller
              name="mail"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  addonBefore={
                    <p style={{ width: "80px", fontWeight: "bold" }}>Email</p>
                  }
                  variant="filled"
                  size="middle"
                  name="mail"
                  type="email"
                  status={field.value.length < 10 ? "error" : ""}
                  value={field.value || ""}
                  onChange={(e) => {
                    setValue("mail", e.target.value.trim());
                  }}
                />
              )}
            />
             {errors.mail && (
                  <p style={{ color: "red" }}>
                    {(errors?.mail?.message as ReactNode) ?? ""}
                  </p>
                )}

          </Grid>
          <Grid item xs={12}>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <InputNumber
                  prefix="+593"
                  addonBefore={
                    <p style={{ width: "80px", fontWeight: "bold" }}>Celular</p>
                  }
                  style={{ width: "100%" }}
                  placeholder="988776655"
                  variant="filled"
                  size="middle"
                  type="tel"
                  name="phone"
                  // status={form.phone.length < 9 ? "error" : ""}
                  value={field.value || ""}
                  onChange={(value) => {
                    setValue("phone", value);
                  }}
                />
              )}
            />
             {errors.phone && (
                  <p style={{ color: "red" }}>
                    {(errors?.phone?.message as ReactNode) ?? ""}
                  </p>
                )}

          </Grid>
          <Grid item xs={12}>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                defaultValue="Cargo"
                readOnly
                style={{
                  width: "103px",
                  fontWeight: "bold",
                  textAlign: "center",
                }}
                variant="filled"
              />
              <Controller
                name="position"
                control={control}
                render={({ field }) => (
                  <Select
                    style={{ width: "calc(100% - 103px)" }}
                    value={field.value || ""}
                    variant="filled"
                    allowClear
                    showSearch={false}
                    onChange={(value) => {
                      if (!value) {
                        setValue("position", "");
                        setValue("positionId", null);
                        return;
                      }

                      const fill = positions.find((el) => el.name === value);
                      setValue("position", value);
                      setValue("positionId", fill!.id);
                    }}
                    options={positions.map((el) => ({
                      label: el.name,
                      value: el.name,
                    }))}
                  />
                )}
              />
            </Space.Compact>
            {errors.area && (
                  <p style={{ color: "red" }}>
                    {(errors?.area?.message as ReactNode) ?? ""}
                  </p>
                )}
          </Grid>
          <Grid item xs={12}>
            <Divider dashed style={{ marginBottom: "5px" }}>
              ⚠️ Acceso 🔑
            </Divider>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="user"
              control={control}
              rules={{ required: true, minLength: 5, maxLength: 20 }}
              render={({ field }) => (
                <Input
                  addonBefore={
                    <p style={{ width: "80px", fontWeight: "bold" }}>Usuario</p>
                  }
                  value={field.value || ""}
                  onChange={(e) => {
                    setValue("user", e.target.value.trim());
                  }}
                  status={field.value.length < 5 ? "error" : ""}
                  maxLength={20}
                />
              )}
            />
             {errors.user && (
                  <p style={{ color: "red" }}>
                    {(errors?.user?.message as ReactNode) ?? ""}
                  </p>
                )}

          </Grid>
          <Grid item xs={12}>
            <Popconfirm
              title="Resetear Password"
              description={
                <p>
                  <p style={{ fontSize: "12px" }}>
                    <p style={{ color: "red" }}>
                      <b>Seguro que desea cambiar la clave del Usuario?</b>
                    </p>
                    <p>Se generará una nueva clave y se enviará por email</p>
                  </p>
                </p>
              }
              onConfirm={handleResetPassword}
              // onCancel={() => message.error("Acción Anulada")}
              okText="SI"
              cancelText="No"
              okButtonProps={{ loading: confirmPassword }}
              placement="top"
            >
              {form.id ? (
                <Button
                  loading={isProcessing}
                  type="primary"
                  danger
                  icon={<SyncLockIcon />}
                  style={{ fontSize: "12px", width: "100%" }}
                >
                  Reset Password
                </Button>
              ) : null}
            </Popconfirm>
          </Grid>
          {errors.docTypeId && (
                  <p style={{ color: "red" }}>
                    {(errors?.docTypeId?.message as ReactNode) ?? ""}
                  </p>
                )}
          {!id ? (
            <Grid item xs={12} sx={{ marginTop: "5px" }}>
              <Alert
                type="warning"
                message="Se enviará al email ingresado la clave de acceso"
                showIcon
                closable={false}
                style={{ width: "100%", fontSize: "12px", fontWeight: "bold" }}
              />
            </Grid>
          ) : null}
        </Grid>
      </form>
    </Modal>
  );
};
